import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { apiFetch } from '../../../utils/apiFetch';
import BattleRoomDialog from './BattleRoomDialog';
import './SceneLifecycleDialog.css';

const mutationId = (operation) => {
  const id = typeof window !== 'undefined' && typeof window.crypto?.randomUUID === 'function'
    ? window.crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
  return `scene:${operation}:${id}`;
};

const readResponse = async (response) => {
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(payload?.error?.message || payload?.error || 'Scene operation failed.');
    error.code = payload?.error?.code;
    error.details = payload?.error?.details;
    throw error;
  }
  return payload;
};

const SceneLifecycleDialog = ({ visible, sessionId, revision = 0, onRequestClose }) => {
  const [scenes, setScenes] = useState([]);
  const [currentRevision, setCurrentRevision] = useState(revision);
  const [newSceneName, setNewSceneName] = useState('');
  const [editingSceneId, setEditingSceneId] = useState(null);
  const [nameDraft, setNameDraft] = useState('');
  const [pendingAction, setPendingAction] = useState(null);
  const [error, setError] = useState('');
  const [confirmation, setConfirmation] = useState(null);
  const [transitionPreview, setTransitionPreview] = useState(null);
  const [transitionPolicy, setTransitionPolicy] = useState({
    actors: 'all',
    selectedActorIds: [],
    actorState: 'preserve',
    effects: 'carry',
    effectDecisions: {},
    initiative: 'preserve',
    combatStatus: 'active',
    camera: 'destination_start',
  });

  const workingScene = useMemo(() => scenes.find((scene) => scene.working) || null, [scenes]);
  const publishedScene = useMemo(() => scenes.find((scene) => scene.published) || null, [scenes]);
  const transitionActors = useMemo(() => transitionPreview?.actorOptions || publishedScene?.transitionActors || [], [publishedScene, transitionPreview]);
  const movingActors = useMemo(() => transitionPolicy.actors === 'none' ? [] : transitionPolicy.actors === 'all'
    ? transitionActors
    : transitionActors.filter((actor) => transitionPolicy.selectedActorIds.includes(actor.tokenId)), [transitionActors, transitionPolicy.actors, transitionPolicy.selectedActorIds]);
  const transitionPolicyComplete = transitionPolicy.actors !== 'selected' || transitionPolicy.selectedActorIds.length > 0;
  const effectReviewComplete = transitionPolicy.effects !== 'review' || movingActors
    .filter((actor) => Number(actor.effectCount) > 0)
    .every((actor) => ['carry', 'end'].includes(transitionPolicy.effectDecisions[actor.tokenId]));

  const applyPayload = useCallback((payload) => {
    setScenes(Array.isArray(payload?.scenes) ? payload.scenes : []);
    if (Number.isSafeInteger(Number(payload?.revision))) setCurrentRevision(Number(payload.revision));
  }, []);

  const loadScenes = useCallback(async () => {
    if (!sessionId) return;
    setPendingAction('load');
    setError('');
    try {
      const response = await apiFetch(`/api/combat/${sessionId}/scenes`, { method: 'GET', requireAuth: true });
      applyPayload(await readResponse(response));
    } catch (cause) {
      setError(cause?.message || 'Failed to load campaign scenes.');
    } finally {
      setPendingAction(null);
    }
  }, [applyPayload, sessionId]);

  useEffect(() => {
    if (visible) loadScenes();
    else { setConfirmation(null); setTransitionPreview(null); }
  }, [loadScenes, visible]);

  useEffect(() => {
    if (Number.isSafeInteger(Number(revision)) && Number(revision) > currentRevision) {
      setCurrentRevision(Number(revision));
    }
  }, [currentRevision, revision]);

  const runOperation = useCallback(async (operation, path, body = {}) => {
    if (!sessionId || pendingAction) return null;
    setPendingAction(operation);
    setError('');
    try {
      const response = await apiFetch(`/api/combat/${sessionId}${path}`, {
        method: operation === 'rename' ? 'PATCH' : 'POST',
        requireAuth: true,
        body: JSON.stringify({
          ...body,
          expectedRevision: currentRevision,
          mutationId: mutationId(operation),
        }),
      });
      const payload = await readResponse(response);
      applyPayload(payload);
      return payload;
    } catch (cause) {
      setError(cause?.message || `Failed to ${operation} scene.`);
      if (cause?.code === 'REVISION_CONFLICT') {
        const current = Number(cause?.details?.currentRevision);
        if (Number.isSafeInteger(current)) setCurrentRevision(current);
        window.setTimeout(loadScenes, 0);
      }
      return null;
    } finally {
      setPendingAction(null);
    }
  }, [applyPayload, currentRevision, loadScenes, pendingAction, sessionId]);

  const createScene = async (event) => {
    event.preventDefault();
    const name = newSceneName.trim();
    if (!name) return;
    const result = await runOperation('create', '/scenes', { name });
    if (result) setNewSceneName('');
  };

  const saveRename = async (sceneId) => {
    const name = nameDraft.trim();
    if (!name) return;
    const result = await runOperation('rename', `/scenes/${sceneId}`, { name });
    if (result) {
      setEditingSceneId(null);
      setNameDraft('');
    }
  };

  const confirmLifecycleOperation = async () => {
    if (!confirmation) return;
    const selected = confirmation;
    setConfirmation(null);
    await runOperation(selected.operation, selected.path);
  };

  const beginTransition = (scene) => {
    setTransitionPreview(null);
    setError('');
    setConfirmation({ operation: 'transition', sceneId: scene.sceneId, sceneName: scene.name });
  };

  const reviewTransition = async () => {
    if (!sessionId || !confirmation?.sceneId || pendingAction) return;
    if (!transitionPolicyComplete || !effectReviewComplete) {
      setError(!transitionPolicyComplete ? 'Select at least one party actor to move.' : 'Review the active effects for every affected actor.');
      return;
    }
    setPendingAction('transition-preview');
    setError('');
    try {
      const response = await apiFetch(`/api/combat/${sessionId}/scenes/${confirmation.sceneId}/transition/preview`, {
        method: 'POST',
        requireAuth: true,
        body: JSON.stringify({ transitionPolicy }),
      });
      const payload = await readResponse(response);
      setTransitionPreview(payload.preview);
    } catch (cause) {
      setError(cause?.message || 'The scene transition could not be reviewed.');
    } finally {
      setPendingAction(null);
    }
  };

  const publishTransition = async () => {
    if (!sessionId || !confirmation?.sceneId || !transitionPreview || pendingAction) return;
    setPendingAction('transition');
    setError('');
    try {
      const response = await apiFetch(`/api/combat/${sessionId}/scenes/${confirmation.sceneId}/transition`, {
        method: 'POST',
        requireAuth: true,
        body: JSON.stringify({
          transitionPolicy,
          previewRevision: transitionPreview.computedAtRevision,
          destinationSceneRevision: transitionPreview.destinationSceneRevision,
          expectedRevision: currentRevision,
          mutationId: mutationId('transition'),
        }),
      });
      applyPayload(await readResponse(response));
      setConfirmation(null);
      setTransitionPreview(null);
    } catch (cause) {
      setError(cause?.message || 'The scene could not be published to the table.');
      setTransitionPreview(null);
      if (cause?.code === 'REVISION_CONFLICT' || cause?.code === 'SCENE_TRANSITION_PREVIEW_STALE') window.setTimeout(loadScenes, 0);
    } finally {
      setPendingAction(null);
    }
  };

  return (
    <>
      <BattleRoomDialog
        visible={visible && !confirmation}
        eyebrow="Campaign scenes"
        title="Scene manager"
        description={publishedScene
          ? `Players see ${publishedScene.name}. You are preparing ${workingScene?.name || publishedScene.name}.`
          : 'Create and prepare campaign scenes for this BattleRoom.'}
        onRequestClose={pendingAction ? undefined : onRequestClose}
      >
      <div className="scene-lifecycle">
        <form className="scene-lifecycle__create" onSubmit={createScene}>
          <label className="battle-room-dialog__field">
            <span className="battle-room-dialog__label">New scene name</span>
            <span className="scene-lifecycle__create-row">
              <input
                className="battle-room-dialog__input"
                value={newSceneName}
                maxLength={80}
                onChange={(event) => setNewSceneName(event.target.value)}
                placeholder="Lower vault"
                data-battle-dialog-autofocus
              />
              <button
                type="submit"
                className="battle-room-dialog__button battle-room-dialog__button--primary"
                disabled={!newSceneName.trim() || Boolean(pendingAction)}
              >
                Create
              </button>
            </span>
          </label>
          <p className="battle-room-dialog__hint">New scenes begin with the current grid settings and an empty map. Duplicate a scene to preserve its prepared contents.</p>
        </form>

        {error && <p className="scene-lifecycle__message scene-lifecycle__message--error" role="alert">{error}</p>}
        {pendingAction === 'load' && <p className="scene-lifecycle__message" role="status">Loading scenes…</p>}

        <div className="scene-lifecycle__list" aria-label="Campaign scenes">
          {scenes.map((scene) => (
            <article className={`scene-lifecycle__row ${scene.working ? 'is-active' : ''}`} key={scene.sceneId}>
              <div className="scene-lifecycle__identity">
                {editingSceneId === scene.sceneId ? (
                  <span className="scene-lifecycle__rename">
                    <input
                      className="battle-room-dialog__input"
                      value={nameDraft}
                      maxLength={80}
                      aria-label={`Rename ${scene.name}`}
                      onChange={(event) => setNameDraft(event.target.value)}
                    />
                    <button type="button" className="scene-lifecycle__compact" disabled={Boolean(pendingAction)} onClick={() => saveRename(scene.sceneId)}>Save</button>
                    <button type="button" className="scene-lifecycle__compact" onClick={() => setEditingSceneId(null)}>Cancel</button>
                  </span>
                ) : (
                  <>
                    <strong>{scene.name}</strong>
                    <span>{scene.status === 'archived' ? 'Archived' : [scene.published ? 'Live for players' : null, scene.working ? 'Open for private preparation' : null].filter(Boolean).join(' · ') || `Draft · revision ${scene.revision}`}</span>
                  </>
                )}
              </div>

              {editingSceneId !== scene.sceneId && (
                <div className="scene-lifecycle__actions">
                  {!scene.working && scene.status !== 'archived' && (
                    <button
                      type="button"
                      className="scene-lifecycle__compact"
                      disabled={Boolean(pendingAction)}
                      onClick={() => runOperation('open', `/scenes/${scene.sceneId}/open`)}
                    >
                      Open Privately
                    </button>
                  )}
                  {!scene.published && scene.status !== 'archived' && (
                    <button type="button" className="scene-lifecycle__compact scene-lifecycle__compact--primary" disabled={Boolean(pendingAction)} onClick={() => beginTransition(scene)}>Publish…</button>
                  )}
                  <button type="button" className="scene-lifecycle__compact" disabled={Boolean(pendingAction)} onClick={() => { setEditingSceneId(scene.sceneId); setNameDraft(scene.name); }}>Rename</button>
                  <button type="button" className="scene-lifecycle__compact" disabled={Boolean(pendingAction)} onClick={() => runOperation('duplicate', `/scenes/${scene.sceneId}/duplicate`, { name: `${scene.name} Copy`.slice(0, 80) })}>Duplicate</button>
                  {scene.status === 'archived' ? (
                    <button type="button" className="scene-lifecycle__compact" disabled={Boolean(pendingAction)} onClick={() => runOperation('restore', `/scenes/${scene.sceneId}/restore`)}>Restore</button>
                  ) : (
                    <button
                      type="button"
                      className="scene-lifecycle__compact scene-lifecycle__compact--danger"
                      disabled={scene.working || scene.published || Boolean(pendingAction)}
                      title={scene.working || scene.published ? 'Open another draft and publish another scene before archiving this one' : 'Archive scene'}
                      onClick={() => setConfirmation({ operation: 'archive', path: `/scenes/${scene.sceneId}/archive`, sceneName: scene.name })}
                    >
                      Archive
                    </button>
                  )}
                </div>
              )}
            </article>
          ))}
          {!pendingAction && scenes.length === 0 && <p className="scene-lifecycle__message">No scenes are available yet.</p>}
        </div>
      </div>
      </BattleRoomDialog>

      <BattleRoomDialog
        visible={Boolean(confirmation)}
        eyebrow="Campaign scenes"
        title={confirmation?.operation === 'transition' ? 'Publish scene to the table?' : 'Archive this scene?'}
        description={confirmation?.operation === 'transition'
          ? 'Review exactly what carries into the destination before every connected participant moves there.'
          : 'The scene will leave the active list but remain recoverable from this manager.'}
        onRequestClose={pendingAction ? undefined : () => setConfirmation(null)}
        footer={(
          <>
            <button type="button" className="battle-room-dialog__button battle-room-dialog__button--quiet" disabled={Boolean(pendingAction)} onClick={() => { setConfirmation(null); setTransitionPreview(null); }}>Cancel</button>
            <button type="button" className="battle-room-dialog__button battle-room-dialog__button--primary" data-battle-dialog-autofocus disabled={Boolean(pendingAction) || (confirmation?.operation === 'transition' && (!transitionPolicyComplete || !effectReviewComplete))} onClick={confirmation?.operation === 'transition' ? (transitionPreview ? publishTransition : reviewTransition) : confirmLifecycleOperation}>
              {confirmation?.operation === 'transition' ? (transitionPreview ? 'Publish for Everyone' : 'Review Transition') : 'Archive Scene'}
            </button>
          </>
        )}
      >
        <p className="battle-room-dialog__note">{confirmation?.sceneName}</p>
        {confirmation?.operation === 'transition' && (
          <div className="scene-transition">
            <div className="scene-transition__grid">
              <label>Actors<select value={transitionPolicy.actors} onChange={(event) => { setTransitionPreview(null); setTransitionPolicy((current) => ({ ...current, actors: event.target.value })); }}><option value="all">Move all party actors</option><option value="selected">Move selected party actors</option><option value="none">Use destination actors</option></select></label>
              <label>Actor HP & resources<select value={transitionPolicy.actorState} disabled={transitionPolicy.actors === 'none'} onChange={(event) => { setTransitionPreview(null); setTransitionPolicy((current) => ({ ...current, actorState: event.target.value })); }}><option value="preserve">Carry current values</option><option value="destination">Restore destination values</option></select></label>
              <label>Effects<select value={transitionPolicy.effects} disabled={transitionPolicy.actors === 'none'} onChange={(event) => { setTransitionPreview(null); setTransitionPolicy((current) => ({ ...current, effects: event.target.value })); }}><option value="carry">Carry current effects</option><option value="end">End current effects</option><option value="review">Review each affected actor</option></select></label>
              <label>Initiative<select value={transitionPolicy.initiative} onChange={(event) => { const initiative = event.target.value; setTransitionPreview(null); setTransitionPolicy((current) => ({ ...current, initiative, ...(initiative === 'end' ? { combatStatus: 'ended' } : current.combatStatus === 'ended' ? { combatStatus: 'paused' } : {}) })); }}><option value="preserve">Keep initiative and turn</option><option value="reset">Reset initiative</option><option value="end">End initiative</option></select></label>
              <label>Combat status<select value={transitionPolicy.combatStatus} onChange={(event) => { const combatStatus = event.target.value; setTransitionPreview(null); setTransitionPolicy((current) => ({ ...current, combatStatus, ...(combatStatus === 'ended' ? { initiative: 'end' } : current.initiative === 'end' ? { initiative: 'reset' } : {}) })); }}><option value="active">Active</option><option value="paused">Paused</option><option value="ended">Ended</option></select></label>
              <label>Camera<select value={transitionPolicy.camera} onChange={(event) => { setTransitionPreview(null); setTransitionPolicy((current) => ({ ...current, camera: event.target.value })); }}><option value="destination_start">Use destination start</option><option value="preserve">Keep current view</option></select></label>
            </div>
            {transitionPolicy.actors === 'selected' && (
              <fieldset className="scene-transition__actors">
                <legend>Select party actors</legend>
                {transitionActors.map((actor) => <label key={actor.tokenId}><input type="checkbox" checked={transitionPolicy.selectedActorIds.includes(actor.tokenId)} onChange={(event) => { setTransitionPreview(null); setTransitionPolicy((current) => ({ ...current, selectedActorIds: event.target.checked ? [...current.selectedActorIds, actor.tokenId] : current.selectedActorIds.filter((tokenId) => tokenId !== actor.tokenId) })); }} /> {actor.name}</label>)}
                {transitionActors.length === 0 && <p>No eligible party actors are in the published scene.</p>}
              </fieldset>
            )}
            {transitionPolicy.effects === 'review' && movingActors.some((actor) => Number(actor.effectCount) > 0) && (
              <fieldset className="scene-transition__actors">
                <legend>Review active effects</legend>
                {movingActors.filter((actor) => Number(actor.effectCount) > 0).map((actor) => <label key={actor.tokenId}>{actor.name} ({actor.effectCount})<select aria-label={`${actor.name} effects`} value={transitionPolicy.effectDecisions[actor.tokenId] || ''} onChange={(event) => { setTransitionPreview(null); setTransitionPolicy((current) => ({ ...current, effectDecisions: { ...current.effectDecisions, [actor.tokenId]: event.target.value } })); }}><option value="">Choose…</option><option value="carry">Carry effects</option><option value="end">End effects</option></select></label>)}
              </fieldset>
            )}
            {transitionPreview && <dl className="scene-transition__preview" aria-label="Scene transition preview"><div><dt>From</dt><dd>{transitionPreview.sourceSceneName}</dd></div><div><dt>To</dt><dd>{transitionPreview.destinationSceneName}</dd></div><div><dt>Actors moved</dt><dd>{transitionPreview.summary?.actorsMoved || 0}{transitionPreview.summary?.actorNames?.length ? ` — ${transitionPreview.summary.actorNames.join(', ')}` : ''}</dd></div><div><dt>Destination round</dt><dd>{transitionPreview.summary?.destinationRound || 1}</dd></div></dl>}
            {error && <p className="scene-lifecycle__message scene-lifecycle__message--error" role="alert">{error}</p>}
          </div>
        )}
      </BattleRoomDialog>
    </>
  );
};

SceneLifecycleDialog.propTypes = {
  visible: PropTypes.bool,
  sessionId: PropTypes.string,
  revision: PropTypes.number,
  onRequestClose: PropTypes.func.isRequired,
};

export default SceneLifecycleDialog;
